import React, { useState, useEffect, useRef } from "react";
import { apiFetch } from "../api";
import useCollapse from "./useCollapse";

// Inline parseErr (originally in Admin.js).  Tiny helper kept local so this
// panel doesn't pull a dependency it doesn't need.
function parseErr(e, fallback) {
  try { return JSON.parse(e.message).detail || fallback; } catch { return fallback; }
}

// ============================================================================
// UNIFIED VCA PIPELINE PANEL (Chunk 8D; relocated to System Admin in SA-B)
// ----------------------------------------------------------------------------
// Replaces the old side-by-side VcaSyncPanel + VcaDataPanel. One button runs
// the complete pipeline end to end:
//
//     VCA API  --sync.py-->  vca_* mirror  --reconcile_vca()-->  native tables
//
// The backend wires both halves through a pipeline_runs row; this panel is
// the single read/write surface. Live polling, coverage scoreboard, recent
// runs history with expandable per-run breakdown.  Coverage scoreboard +
// "fields used" columns update automatically after each run.
// ============================================================================
export default function PipelinePanel({ onReconciled }) {
  // ---------- state ----------
  const { open, toggle, chevron } = useCollapse(false); // collapsed by default — run/log on demand
  const [status, setStatus] = useState(null);      // /admin/pipeline/status payload
  const [log, setLog] = useState([]);              // /admin/pipeline/log rows
  const [loading, setLoading] = useState(true);
  const [logLoading, setLogLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [starting, setStarting] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const pollRef = useRef(null);

  // ---------- data loaders ----------
  const loadStatus = async () => {
    try {
      const s = await apiFetch("/admin/pipeline/status");
      setStatus(s);
      return s;
    } catch (e) {
      setErr(parseErr(e, "Could not load pipeline status"));
      return null;
    } finally {
      setLoading(false);
    }
  };
  const loadLog = () => {
    setLogLoading(true);
    apiFetch("/admin/pipeline/log?limit=50")
      .then(rows => { setLog(rows); setLogLoading(false); })
      .catch(() => { setLogLoading(false); });
  };
  useEffect(() => { loadStatus(); loadLog(); }, []);

  // Poll only while a pipeline is live; refresh the log in the same beat so
  // the history table picks up phase/status transitions without a manual click.
  useEffect(() => {
    if (status?.running) {
      if (!pollRef.current) {
        pollRef.current = setInterval(() => { loadStatus(); loadLog(); }, 5000);
      }
    } else if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
      // A run just finished — pull the history once more so the new row lands.
      loadLog();
      onReconciled?.();
    }
    return () => {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    };
  }, [status?.running]);

  // ---------- actions ----------
  const run = async () => {
    if (!window.confirm(
      "Run the full VCA pipeline now?\n\n" +
      "1. Syncs every claim and child endpoint from VCA into the vca_* mirror " +
      "(FileMaker + Postgres). 20–60 minutes.\n" +
      "2. Extracts the mirror into native companies / contacts / users. " +
      "New rows are flagged for admin review.\n\n" +
      "Automatic run fires nightly at 00:00 Pacific. Use this for an out-of-cycle refresh."
    )) return;
    setStarting(true); setErr(null);
    try {
      await apiFetch("/admin/pipeline/run", { method: "POST" });
      await loadStatus();
      loadLog();
    } catch (e) {
      setErr(parseErr(e, "Could not start pipeline"));
    } finally {
      setStarting(false);
    }
  };

  // ---------- formatting helpers ----------
  const fmtDateTime = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString(); } catch { return iso; }
  };
  const fmtDuration = (a, b) => {
    if (!a || !b) return "—";
    const ms = new Date(b) - new Date(a);
    if (isNaN(ms) || ms < 0) return "—";
    if (ms < 1000) return `${ms}ms`;
    const s = Math.floor(ms / 1000);
    if (s < 60) return `${s}s`;
    const m = Math.floor(s / 60), rem = s % 60;
    if (m < 60) return `${m}m ${rem}s`;
    const h = Math.floor(m / 60), rm = m % 60;
    return `${h}h ${rm}m`;
  };
  const fmtNum = (n) => (n === null || n === undefined ? "—" : Number(n).toLocaleString());

  // Pipeline status → badge style (pipeline_runs.status: running/success/partial/error)
  const pipelineBadge = (s) => {
    const base = {padding:"2px 8px",borderRadius:10,fontSize:11,fontWeight:600,textTransform:"uppercase",letterSpacing:0.3};
    if (s === "success") return {...base, background:"#d1fae5", color:"#065f46"};
    if (s === "error")   return {...base, background:"#fee2e2", color:"#991b1b"};
    if (s === "partial") return {...base, background:"#fed7aa", color:"#9a3412"};
    return {...base, background:"#fef3c7", color:"#92400e"}; // running
  };
  // Per-table mapping status → subtle row tint
  const coverageBadge = (s) => {
    const base = {padding:"2px 6px",borderRadius:4,fontSize:10,fontWeight:600,textTransform:"uppercase",letterSpacing:0.3};
    if (s === "active")   return {...base, background:"#d1fae5", color:"#065f46"};
    if (s === "pending")  return {...base, background:"#fef3c7", color:"#92400e"};
    if (s === "deferred") return {...base, background:"#e0e7ff", color:"#3730a3"};
    if (s === "metadata") return {...base, background:"#dbeafe", color:"#1e40af"};
    return {...base, background:"#f3f4f6", color:"#4b5563"}; // omitted / unknown
  };

  // ---------- derived ----------
  const running = !!status?.running;
  const latest = status?.latest;
  const coverage = status?.coverage || [];
  const totals = status?.coverage_totals || {};
  // live_phase is 'sync' or 'extract' while running; falls back to latest row's phase otherwise
  const phase = status?.live_phase || latest?.phase;

  return (
    <div>
      {/* ---------- Run button ---------- */}
      <div className="section-header" onClick={toggle} style={{cursor:"pointer"}}>
        <span className="section-title" style={{display:"flex",alignItems:"center",gap:10}}>{chevron}VCA Pipeline</span>
      </div>
      {open && (<>
      <div className="card" style={{padding:16,marginBottom:16}}>
        <div style={{display:"flex",alignItems:"flex-start",gap:16}}>
          <div style={{flex:1}}>
            <div style={{fontWeight:600,marginBottom:4}}>Run Full Pipeline</div>
            <div style={{fontSize:12,color:"var(--text2)",lineHeight:1.5}}>
              One button, both halves: <strong>sync</strong> pulls every claim and child endpoint from VCA into the
              <code> vca_*</code> mirror, then <strong>extraction</strong> promotes those rows into native
              <strong> companies</strong>, <strong>contacts</strong>, and <strong>users</strong>. New rows land in the
              Review Queue. Takes 20–60 minutes end to end.
            </div>
            <div style={{fontSize:11,color:"var(--text3)",marginTop:6}}>
              Automatic run: <strong>00:00 Pacific</strong> nightly. Use this for an out-of-cycle refresh.
            </div>
          </div>
          <button className="btn btn-primary" onClick={run} disabled={starting || running || loading}>
            {starting ? "Starting…" : running ? `Running (${phase})…` : "Run Pipeline"}
          </button>
        </div>
        {err && <div className="alert-error" style={{marginTop:12}}>{err}</div>}
      </div>

      {/* ---------- Live progress pane (sync phase only shows log tail) ---------- */}
      {running && (
        <div className="card" style={{padding:16,marginBottom:16,borderLeft:"4px solid #f59e0b"}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
            <div style={{fontWeight:600}}>
              Pipeline in progress — phase: <code>{phase}</code> (run #{status.live_run_id})
            </div>
            <button className="btn btn-outline btn-sm" onClick={loadStatus}>Refresh now</button>
          </div>
          {phase === "sync" ? (
            status.current_log_tail ? (
              <pre style={{
                fontSize:11, lineHeight:1.4,
                background:"#111827", color:"#d1d5db",
                padding:12, borderRadius:4, margin:0,
                maxHeight:240, overflow:"auto",
                whiteSpace:"pre-wrap"
              }}>{status.current_log_tail}</pre>
            ) : (
              <div style={{fontSize:12,color:"var(--text3)"}}>
                Sync starting — log not yet written. Polling every 5 seconds.
              </div>
            )
          ) : (
            <div style={{fontSize:12,color:"var(--text2)"}}>
              Sync complete. Extraction is promoting mirror rows into native tables now…
            </div>
          )}
        </div>
      )}

      {/* ---------- Coverage scoreboard (doubles as the "Last Run" summary) ---------- */}
      <div className="section-header" style={{marginTop:8}}>
        <span className="section-title">Last Run — VCA Coverage</span>
        {latest && (
          <span style={{fontSize:11,color:"var(--text3)"}}>
            Finished {fmtDateTime(latest.finished_at)} · {fmtDuration(latest.started_at, latest.finished_at)}
            {" · "}<span style={pipelineBadge(latest.status)}>{latest.status}</span>
          </span>
        )}
      </div>
      {!loading && coverage.length > 0 && (
        <div className="card" style={{padding:0,marginBottom:16}}>
          {/* Top-line totals band */}
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(140px,1fr))",gap:12,padding:12,borderBottom:"1px solid var(--border)",background:"var(--panel)"}}>
            <div>
              <div style={{fontSize:11,textTransform:"uppercase",color:"var(--text3)",letterSpacing:0.5}}>Records Synced</div>
              <div style={{fontSize:13,fontWeight:600}}>{fmtNum(totals.records_synced)}</div>
            </div>
            <div>
              <div style={{fontSize:11,textTransform:"uppercase",color:"var(--text3)",letterSpacing:0.5}}>Records Extracted</div>
              <div style={{fontSize:13,fontWeight:600}}>{fmtNum(totals.records_extracted)}</div>
            </div>
            <div>
              <div style={{fontSize:11,textTransform:"uppercase",color:"var(--text3)",letterSpacing:0.5}}>Fields Synced</div>
              <div style={{fontSize:13,fontWeight:600}}>{fmtNum(totals.fields_synced)}</div>
            </div>
            <div>
              <div style={{fontSize:11,textTransform:"uppercase",color:"var(--text3)",letterSpacing:0.5}}>Fields Used</div>
              <div style={{fontSize:13,fontWeight:600}}>{fmtNum(totals.fields_used)}</div>
            </div>
            <div>
              <div style={{fontSize:11,textTransform:"uppercase",color:"var(--text3)",letterSpacing:0.5}}>Tables</div>
              <div style={{fontSize:13,fontWeight:600}}>
                {totals.tables_active || 0} active
                <span style={{color:"var(--text3)",fontWeight:400}}> · {totals.tables_pending || 0} pending · {totals.tables_deferred || 0} deferred · {totals.tables_omitted || 0} omitted</span>
              </div>
            </div>
          </div>

          {/* Per-table scoreboard */}
          <div style={{maxHeight:420,overflow:"auto"}}>
            <table style={{width:"100%",fontSize:12}}>
              <thead>
                <tr style={{background:"#f9fafb",position:"sticky",top:0,zIndex:1}}>
                  <th style={{textAlign:"left",padding:"6px 10px"}}>Table</th>
                  <th style={{textAlign:"left",padding:"6px 10px"}}>Native Target</th>
                  <th style={{textAlign:"right",padding:"6px 10px"}}>Records Synced</th>
                  <th style={{textAlign:"right",padding:"6px 10px"}}>Records Extracted</th>
                  <th style={{textAlign:"right",padding:"6px 10px"}}>Fields Synced</th>
                  <th style={{textAlign:"right",padding:"6px 10px"}}>Fields Used</th>
                  <th style={{textAlign:"left",padding:"6px 10px"}}>Status</th>
                </tr>
              </thead>
              <tbody>
                {coverage.map(row => {
                  const fieldGap = (row.fields_synced || 0) - (row.fields_used || 0);
                  return (
                    <tr key={row.vca_table} style={{borderTop:"1px solid var(--border)"}} title={row.note || ""}>
                      <td style={{padding:"4px 10px",fontFamily:"var(--mono, monospace)"}}>{row.vca_table}</td>
                      <td style={{padding:"4px 10px",color:"var(--text2)"}}>{row.native_target}</td>
                      <td style={{padding:"4px 10px",textAlign:"right"}}>{fmtNum(row.records_synced)}</td>
                      <td style={{padding:"4px 10px",textAlign:"right",color:(row.records_extracted||0) > 0 ? undefined : "var(--text3)"}}>{fmtNum(row.records_extracted)}</td>
                      <td style={{padding:"4px 10px",textAlign:"right"}}>{fmtNum(row.fields_synced)}</td>
                      <td style={{padding:"4px 10px",textAlign:"right",color:fieldGap > 0 && row.status === "active" ? "#b45309" : undefined,fontWeight:fieldGap > 0 && row.status === "active" ? 600 : 400}}>
                        {fmtNum(row.fields_used)}
                      </td>
                      <td style={{padding:"4px 10px"}}><span style={coverageBadge(row.status)}>{row.status}</span></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div style={{fontSize:11,color:"var(--text3)",padding:"8px 12px",borderTop:"1px solid var(--border)"}}>
            <strong>Fields synced</strong> is sampled from JSONB keys in each mirror (first 500 rows).
            <strong> Fields used</strong> counts keys reconcile_vca reads during extraction —
            an orange highlight means there are extra fields available we aren't mapping yet. Hover rows for notes.
          </div>
        </div>
      )}

      {/* ---------- Recent pipeline runs ---------- */}
      <div className="section-header" style={{marginTop:24}}>
        <span className="section-title">Recent Runs ({log.length})</span>
        <button className="btn btn-outline btn-sm" onClick={loadLog} disabled={logLoading}>Refresh</button>
      </div>

      {logLoading ? (
        <div className="card" style={{padding:20}}><div className="loading">Loading history...</div></div>
      ) : log.length === 0 ? (
        <div className="card" style={{padding:20,textAlign:"center",color:"var(--text3)",fontSize:12}}>
          No pipeline runs yet. Click “Run Pipeline” above or wait for tonight’s cron.
        </div>
      ) : (
        <div className="card table-wrap">
          <table>
            <thead><tr>
              <th style={{width:28}}></th>
              <th>Started</th>
              <th>Sync</th>
              <th>Extract</th>
              <th>Trigger</th>
              <th>By</th>
              <th>Status</th>
              <th>Summary</th>
            </tr></thead>
            <tbody>
              {log.map(row => {
                const rowKey = `${row.source || "pipeline"}-${row.id}`;
                const open = expandedId === rowKey;
                const sync = row.sync_summary || {};
                const ext = row.extraction_summary || {};
                const extTotal = ["companies","users","tpa_companies","contacts"].reduce((acc, k) => {
                  const g = ext[k]; if (!g) return acc;
                  return acc + (g.inserted || 0) + (g.updated || 0) + (g.linked_existing || 0);
                }, 0);
                return (
                  <React.Fragment key={rowKey}>
                    <tr style={{cursor:"pointer"}} onClick={() => setExpandedId(open ? null : rowKey)}>
                      <td style={{color:"var(--text3)"}}>{open ? "▼" : "▶"}</td>
                      <td style={{whiteSpace:"nowrap"}}>{fmtDateTime(row.started_at)}</td>
                      <td style={{whiteSpace:"nowrap",fontSize:12,color:"var(--text2)"}}>
                        {fmtDuration(row.sync_started_at, row.sync_finished_at)}
                      </td>
                      <td style={{whiteSpace:"nowrap",fontSize:12,color:"var(--text2)"}}>
                        {fmtDuration(row.extraction_started_at, row.extraction_finished_at)}
                      </td>
                      <td>
                        <span className="badge" style={{background: row.triggered_via === "cron" ? "#ede9fe" : "#dbeafe", color: row.triggered_via === "cron" ? "#5b21b6" : "#1e40af"}}>
                          {row.triggered_via}
                        </span>
                      </td>
                      <td>{row.triggered_by_full_name || row.triggered_by_username || <span style={{color:"var(--text3)"}}>system</span>}</td>
                      <td><span style={pipelineBadge(row.status)}>{row.status}</span></td>
                      <td>
                        {row.status === "error"
                          ? <span style={{color:"#991b1b",fontSize:12}}>{(row.error_message || "").slice(0, 80)}{row.error_message && row.error_message.length > 80 ? "…" : ""}</span>
                          : <span style={{fontSize:12,color:"var(--text2)"}}>
                              {sync.total_records ? `${Number(sync.total_records).toLocaleString()} synced` : "—"}
                              {" · "}
                              {extTotal > 0 ? `${extTotal} row${extTotal === 1 ? "" : "s"} touched` : "0 touched"}
                            </span>}
                      </td>
                    </tr>
                    {open && (
                      <tr>
                        <td colSpan={8} style={{background:"var(--panel)",padding:16}}>
                          {row.error_message && (
                            <div style={{marginBottom:12}}>
                              <div style={{fontSize:11,textTransform:"uppercase",color:"var(--text3)",letterSpacing:0.5,marginBottom:4}}>Error</div>
                              <pre style={{fontSize:11,color:"#991b1b",whiteSpace:"pre-wrap",margin:0}}>{row.error_message}</pre>
                            </div>
                          )}
                          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16}}>
                            <div>
                              <div style={{fontSize:11,textTransform:"uppercase",color:"var(--text3)",letterSpacing:0.5,marginBottom:6}}>Sync phase</div>
                              {sync && sync.tables ? (
                                <div style={{border:"1px solid var(--border)",borderRadius:4,background:"#fff",maxHeight:240,overflow:"auto"}}>
                                  <table style={{width:"100%",fontSize:11}}>
                                    <thead><tr style={{background:"#f9fafb"}}>
                                      <th style={{textAlign:"left",padding:"4px 8px"}}>Table</th>
                                      <th style={{textAlign:"right",padding:"4px 8px"}}>Records</th>
                                      <th style={{textAlign:"right",padding:"4px 8px"}}>Errors</th>
                                    </tr></thead>
                                    <tbody>
                                      {sync.tables.map(t => (
                                        <tr key={t.table_name} style={{borderTop:"1px solid var(--border)"}}>
                                          <td style={{padding:"3px 8px"}}>{t.table_name}</td>
                                          <td style={{padding:"3px 8px",textAlign:"right"}}>{(t.record_count||0).toLocaleString()}</td>
                                          <td style={{padding:"3px 8px",textAlign:"right",color:(t.errors||0) > 0 ? "#991b1b" : "var(--text3)"}}>{t.errors || 0}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              ) : (
                                <span style={{fontSize:12,color:"var(--text3)"}}>No sync summary recorded.</span>
                              )}
                            </div>
                            <div>
                              <div style={{fontSize:11,textTransform:"uppercase",color:"var(--text3)",letterSpacing:0.5,marginBottom:6}}>Extraction phase</div>
                              {ext && Object.keys(ext).length > 0 ? (
                                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                                  {["companies","users","tpa_companies","contacts"].map(k => {
                                    const v = ext[k]; if (!v) return null;
                                    return (
                                      <div key={k} style={{border:"1px solid var(--border)",borderRadius:6,padding:8,background:"#fff"}}>
                                        <div style={{fontSize:10,textTransform:"uppercase",color:"var(--text3)",letterSpacing:0.5,marginBottom:4}}>
                                          {k.replace(/_/g," ")}
                                        </div>
                                        {Object.entries(v).map(([key, val]) => (
                                          <div key={key} style={{display:"flex",justifyContent:"space-between",fontSize:11,padding:"1px 0"}}>
                                            <span style={{color:"var(--text2)"}}>{key.replace(/_/g," ")}</span>
                                            <span style={{fontWeight: val > 0 ? 600 : 400, color: val > 0 ? "var(--text1)" : "var(--text3)"}}>{val}</span>
                                          </div>
                                        ))}
                                      </div>
                                    );
                                  })}
                                </div>
                              ) : (
                                <span style={{fontSize:12,color:"var(--text3)"}}>
                                  {row.extraction_log_id ? "No summary recorded." : "Extraction did not run (sync failed or still in progress)."}
                                </span>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      </>)}
    </div>
  );
}
